/**
 * Get nested value from object using dot notation path
 * @param {object} obj - Object to get value from
 * @param {string} path - Dot notation path (e.g., 'user.name')
 * @returns {*} Value at path or undefined
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
}

/**
 * Check if a value matches a condition
 * @param {*} value - Value to check
 * @param {*} condition - Condition to match (can be direct value or operator object)
 * @returns {boolean} Whether value matches condition
 */
function matchesCondition(value, condition) {
  // Direct equality: { field: value }
  if (typeof condition !== 'object' || condition === null) {
    return value === condition;
  }

  // Operator-based conditions
  return Object.entries(condition).every(([operator, operand]) => {
    switch (operator) {
      case '$eq':
        return value === operand;
      case '$ne':
        return value !== operand;
      case '$gt':
        return value > operand;
      case '$gte':
        return value >= operand;
      case '$lt':
        return value < operand;
      case '$lte':
        return value <= operand;
      case '$in':
        return Array.isArray(operand) && operand.includes(value);
      case '$nin':
        return Array.isArray(operand) && !operand.includes(value);
      case '$exists':
        return operand ? value !== undefined : value === undefined;
      case '$regex': {
        const regex = new RegExp(operand);
        return regex.test(String(value));
      }
      default:
        throw new Error(`Unknown operator: ${operator}`);
    }
  });
}

/**
 * Check if a document matches a selector
 * @param {object} doc - Document to check
 * @param {object} selector - Selector with conditions
 * @returns {boolean} Whether document matches selector
 */
export function matchesSelector(doc, selector) {
  return Object.entries(selector).every(([key, condition]) => {
    if (key === '$and') {
      return condition.every(subSelector => matchesSelector(doc, subSelector));
    }
    if (key === '$or') {
      return condition.some(subSelector => matchesSelector(doc, subSelector));
    }
    if (key === '$not') {
      return !matchesSelector(doc, condition);
    }

    // Field condition
    const value = getNestedValue(doc, key);
    return matchesCondition(value, condition);
  });
}

/**
 * Compare two documents based on sort specification
 * @param {object} a - First document
 * @param {object} b - Second document
 * @param {Array<string | object>} sortSpec - Sort specification
 * @returns {number} Comparison result (-1, 0, 1)
 */
export function compareDocuments(a, b, sortSpec) {
  for (const sortField of sortSpec) {
    let field, direction;

    if (typeof sortField === 'string') {
      field = sortField;
      direction = 'asc';
    } else {
      field = Object.keys(sortField)[0];
      direction = sortField[field];
    }

    const aVal = getNestedValue(a, field);
    const bVal = getNestedValue(b, field);

    let comparison = 0;
    if (aVal < bVal) comparison = -1;
    else if (aVal > bVal) comparison = 1;

    if (comparison !== 0) {
      return direction === 'desc' ? -comparison : comparison;
    }
  }

  return 0;
}

/**
 * Apply Mango-like query to an array of documents
 * @param {Array<object>} documents - Array of documents to query
 * @param {object} query - Mango-like query object with selector and sort
 * @param {object} [query.selector] - Filter conditions (Mango operators)
 * @param {Array<string | object>} [query.sort] - Sort specification
 * @returns {Array<object>} Filtered and sorted documents
 */
export function applyQuery(documents, query) {
  if (!query) return documents;

  // Handle non-array input
  if (!Array.isArray(documents)) {
    return [];
  }

  let result = documents;

  // Apply selector (filtering)
  if (query.selector) {
    result = result.filter(doc => matchesSelector(doc, query.selector));
  }

  // Apply sort
  if (query.sort) {
    result = [...result].sort((a, b) => compareDocuments(a, b, query.sort));
  }

  return result;
}
